/**
 * KAN-862: карточка клиента на канбан-доске CRM: аватар, имя (ссылка в карточку
 * клиента), организация, теплота (полоса слева и значок, как в AttentionRow),
 * менеджер, последнее сообщение в две строки, время активности, отметка внимания.
 * На телефоне кнопка "Сменить этап" (onChangeStage), на широком экране этап
 * меняется перетаскиванием.
 * KAN-865: клик по карточке (и Enter в фокусе) открывает детали клиента
 * (onOpen); клики по ссылкам, кнопкам и аватару (data-no-open) не открывают.
 * Space остаётся перетаскиванию. У карточки внимания блок "Требует внимания"
 * со временем, причиной и задачей менеджеру, полоса слева розовая.
 * KAN-874: stageLabel - подпись этапа (общий список внимания мобильной доски).
 * KAN-880: метка фактического этапа (stage_name; stageLabel главнее) и
 * "N дней на этапе"; у взятых в работу (taken_at) "N дней в работе".
 * KAN-882: пометка исхода "Успех" / "Не успех" (kanban-card-outcome); метка
 * состояния лида (kanban-card-state) при showState (виды managers и funnel).
 * KAN-921: у отказа пометка "кто закрыл" (kanban-card-closed-by) по outcome_source.
 */
import { forwardRef, type HTMLAttributes, type KeyboardEvent, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRightLeft, UserRound } from 'lucide-react';
import { ClientAvatar } from '@/components/clients/ClientAvatar';
import { attentionTask, formatTaskLine } from '@/components/dashboard/attentionFormat';
import { LeadTemperatureBadge } from '@/components/leads/LeadTemperatureBadge';
import { formatClientActivity } from '@/lib/clientListFormat';
import { CARD_STATE_LABELS, cardClosedByLabel, cardOutcomeLabel, daysInWorkText, daysOnStageText } from '@/lib/crmPipeline';
import { leadTemperatureStyle } from '@/lib/leadTemperature';
import { cn } from '@/lib/utils';
import type { CrmPipelineCard } from '@/types/crmPipeline';

export interface KanbanCardProps extends HTMLAttributes<HTMLElement> {
  card: CrmPipelineCard;
  href: string;
  /** Ссылка на организацию (только у организации с id), иначе название текстом. */
  companyHref?: string | null;
  onChangeStage?: (card: CrmPipelineCard) => void;
  /** KAN-865: открыть детали клиента (клик по карточке, Enter). */
  onOpen?: (card: CrmPipelineCard) => void;
  dragging?: boolean;
  /** KAN-874: подпись этапа клиента (список вне колонок); нет: без подписи. */
  stageLabel?: string;
  /** KAN-882: метка состояния лида (Требует внимания / Взят в работу / У бота ...). */
  showState?: boolean;
}

const ROLE_PREFIX: Record<string, string> = { user: 'Клиент: ' };

/** Клик пришёл из вложенного интерактивного элемента: детали не открываются. */
const NO_OPEN_SELECTOR = 'a,button,[data-no-open]';

function fromNested(event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>): boolean {
  const target = event.target as Element | null;
  const nested = target?.closest?.(NO_OPEN_SELECTOR);
  return !!nested && nested !== event.currentTarget;
}

function CardAttention({ card }: { card: CrmPipelineCard }) {
  const since = formatClientActivity(card.attention_since);
  const task = attentionTask({ task: card.attention_task });
  return (
    <div data-testid="kanban-card-attention" className="mt-2 space-y-1">
      <span className="inline-flex max-w-full items-center gap-1 rounded bg-rose-100 px-1.5 py-0.5 text-xs font-medium text-rose-700">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">
          Требует внимания
          {since && ` · ${since}`}
        </span>
      </span>
      {card.attention_reason && (
        <p data-testid="kanban-card-reason" className="line-clamp-2 break-words text-xs text-gray-800">
          {card.attention_reason}
        </p>
      )}
      {task && (
        <p className="line-clamp-2 break-words text-xs text-gray-600">
          {formatTaskLine(task)}
          {task.overdue && <span className="ml-1 rounded bg-red-100 px-1 text-red-700">просрочено</span>}
        </p>
      )}
    </div>
  );
}

export const KanbanCard = forwardRef<HTMLElement, KanbanCardProps>(function KanbanCard(
  {
    card,
    href,
    companyHref,
    onChangeStage,
    onOpen,
    dragging = false,
    stageLabel,
    showState = false,
    className,
    onClick,
    onKeyDown,
    ...rest
  },
  ref
) {
  const temperature = leadTemperatureStyle(card.temperature);
  const stageText = stageLabel ?? card.stage_name ?? null;
  const inWork = !!card.taken_at;
  const workDays = inWork ? daysInWorkText(card.days_in_work) : null;
  const stageDays = inWork ? null : daysOnStageText(card.days_on_stage);
  const outcomeText = cardOutcomeLabel(card.outcome);
  const closedByText = card.outcome === 'lost' ? cardClosedByLabel(card.outcome_source) : null;
  const stateText = showState && card.state ? CARD_STATE_LABELS[card.state] : null;
  const activity = formatClientActivity(card.last_message?.at ?? card.last_activity_at);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    onClick?.(event);
    if (!onOpen || event.defaultPrevented || fromNested(event)) return;
    onOpen(card);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    // dnd слушает Space на ручке перетаскивания; Enter открывает детали.
    onKeyDown?.(event);
    if (!onOpen || event.defaultPrevented || event.key !== 'Enter' || fromNested(event)) return;
    event.preventDefault();
    onOpen(card);
  };
  return (
    <article
      ref={ref}
      data-testid="kanban-card"
      className={cn(
        'rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-sm',
        temperature?.row,
        card.attention && 'border-l-4 border-l-rose-500',
        onOpen && 'cursor-pointer hover:border-blue-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
        dragging && 'shadow-lg ring-2 ring-blue-300',
        className
      )}
      {...rest}
      {...(onOpen ? { role: 'button', tabIndex: 0, 'aria-label': `Открыть ${card.name}` } : {})}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span data-no-open className="shrink-0">
          <ClientAvatar avatarUrl={card.avatar_url} name={card.name} size={24} />
        </span>
        <Link to={href} className="min-w-0 flex-1 truncate font-medium text-gray-900 hover:text-blue-600 hover:underline">
          {card.name}
        </Link>
      </div>
      {(stageText || stageDays || workDays || outcomeText || closedByText || stateText) && (
        <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          {outcomeText && (
            <span
              data-testid="kanban-card-outcome"
              className={cn(
                'rounded px-1.5 py-0.5 text-xs font-medium',
                card.outcome === 'won' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-700'
              )}
            >
              {outcomeText}
            </span>
          )}
          {closedByText && (
            <span data-testid="kanban-card-closed-by" className="text-xs text-gray-500">
              {closedByText}
            </span>
          )}
          {stateText && (
            <span data-testid="kanban-card-state" className="rounded bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-700">
              {stateText}
            </span>
          )}
          {stageText && (
            <span
              data-testid="kanban-card-stage"
              className="inline-block max-w-full truncate rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700"
            >
              {stageText}
            </span>
          )}
          {stageDays && (
            <span data-testid="kanban-card-days" className="text-xs text-gray-500">
              {stageDays}
            </span>
          )}
          {workDays && (
            <span data-testid="kanban-card-work-days" className="text-xs font-medium text-blue-700">
              {workDays}
            </span>
          )}
        </div>
      )}
      {card.attention && <CardAttention card={card} />}
      {card.company && (
        <div className="mt-1 truncate text-xs text-gray-500">
          {companyHref ? (
            <Link to={companyHref} className="hover:text-blue-600 hover:underline">
              {card.company.name}
            </Link>
          ) : (
            card.company.name
          )}
        </div>
      )}
      {(card.temperature || card.manager) && (
        <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2">
          <LeadTemperatureBadge level={card.temperature} />
          {card.manager && (
            <span className="inline-flex min-w-0 items-center gap-1 text-xs text-gray-600">
              <UserRound className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{card.manager.name}</span>
            </span>
          )}
        </div>
      )}
      {card.last_message?.text && (
        <p className="mt-2 line-clamp-2 break-words text-gray-600">
          {ROLE_PREFIX[card.last_message.role] ?? ''}
          {card.last_message.text}
        </p>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-xs text-gray-400">{activity}</span>
        {onChangeStage && (
          <button
            type="button"
            onClick={() => onChangeStage(card)}
            className="inline-flex min-h-[36px] items-center gap-1 rounded-md px-2 text-xs font-medium text-blue-600 active:bg-blue-50"
          >
            <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden="true" />
            Сменить этап
          </button>
        )}
      </div>
    </article>
  );
});
